import * as THREE from "three";
import type { Biome } from "../../ui/chapters";
import { GroundMesh } from "../groundMesh";
import { groundAt } from "../terrain";
import { lib } from "./common";
import { flatten, frac } from "./detailParts";

/** The three low-poly scree stones (Phase 3 §6); absent, the gorge simply goes without. */
export const SCREE_MODELS = ["d_scree_1", "d_scree_2", "d_scree_3"];
/** Scree tint per biome (the stones ship neutral warm grey), multiplied per instance with a ±8% jitter. */
export const SCREE_TINT: Readonly<Record<Biome, [number, number, number]>> = { meadow: [1, 0.97, 0.92], marsh: [0.7, 0.76, 0.68], ashlands: [0.46, 0.42, 0.4] };

/**
 * The half-buried scree along every gorge lip and foot, one instanced mesh per stone model that ships (terrain F8): it breaks the two long lines that made a chasm read
 * as a built channel — the clean top edge and the clean bottom edge. Spots come from the wall builder
 * (`GroundMesh.currentGorge`, a few per wall edge). One instanced mesh per stone model for the whole map: a gorge
 * is a few dozen edges, ~150 stones at 80 triangles, three draws.
 */
export function screeMeshes(biome: Biome): THREE.InstancedMesh[] {
  const out: THREE.InstancedMesh[] = [];
  const L = lib();
  const spots = GroundMesh.currentGorge;
  // no gorge, no scree — and no asking the library for the stones: `hq()` on a model the screen's asset set left
  // out (the hub, every level without a chasm) logs a miss and fetches all three for nothing
  if (!L || !spots.length) return out;
  const have = SCREE_MODELS.filter((n) => L.hq(n));
  if (!have.length) return out;
  const tint = SCREE_TINT[biome] ?? SCREE_TINT.meadow;
  type Stone = { x: number; y: number; z: number; yaw: number; s: number; sink: number; b: number };
  const per: Stone[][] = have.map(() => []);
  for (const sp of spots) {
    const n = sp.lip ? (sp.h < 0.55 ? 1 : 2) : 2 + (sp.h > 0.7 ? 1 : 0);
    for (let k = 0; k < n; k++) {
      const h = frac(sp.h * 17.3 + k * 3.1);
      const side = (frac(h * 5.7) - 0.5) * 0.36;            // spread along the wall
      const out = sp.lip ? -frac(h * 2.3) * 0.08 : frac(h * 2.3) * 0.22;
      const x = sp.x - sp.nz * side + sp.nx * out, z = sp.z + sp.nx * side + sp.nz * out;
      const s = sp.lip ? 0.55 + frac(h * 9.1) * 0.55 : 0.8 + frac(h * 9.1) * 0.8;
      const y = sp.lip ? groundAt(x, z) : sp.y;
      per[Math.floor(frac(h * 7.7) * have.length) % have.length].push({ x, y, z, yaw: frac(h * 3.3) * Math.PI * 2, s, sink: sp.lip ? 0.45 : 0.3, b: 0.92 + frac(h * 11.9) * 0.16 });
    }
  }
  have.forEach((name, i) => {
    const stones = per[i];
    if (!stones.length) return;
    const [part] = flatten(L.clone(`hq:${name}`), name);
    part.geometry.computeBoundingBox();
    const tall = (part.geometry.boundingBox!.max.y - part.geometry.boundingBox!.min.y) * part.local.elements[5];
    const inst = new THREE.InstancedMesh(part.geometry, part.material, stones.length);
    inst.castShadow = false; inst.receiveShadow = true;
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), p = new THREE.Vector3(), c = new THREE.Color();
    stones.forEach((st, j) => {
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), st.yaw);
      p.set(st.x, st.y - tall * st.s * st.sink, st.z);
      sc.set(st.s, st.s * (0.8 + st.b * 0.2), st.s);
      m.compose(p, q, sc).multiply(part.local);
      inst.setMatrixAt(j, m);
      inst.setColorAt(j, c.setRGB(tint[0] * st.b, tint[1] * st.b, tint[2] * st.b));
    });
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    inst.computeBoundingSphere();
    out.push(inst);
  });
  return out;
}
